import * as React from 'react';

import { TabIds } from '@containers/ProductDetailContainer/ProductDetail/model';

export const TabSwitcherContext =
  React.createContext<ITabSwitcherProviderState>(null!);

interface ITab {
  id: number;
  ref: React.RefObject<HTMLButtonElement>;
}

export interface ITabSwitcherProviderState {
  tabs: ITab[];
  activeTabId?: number;
  isReviewTabVisibleOnMobile: boolean;
  register: (tabId: number, ref: React.RefObject<HTMLButtonElement>) => void;
  selectNext: () => void;
  selectPrevious: () => void;
  toggleIsReviewTabVisibleOnMobile: () => void;
  setActiveTab: (newActiveTabIndex: TabIds) => void;
}

export const TabSwitcherProvider = ({
  children,
}: React.PropsWithChildren<object>) => {
  const [tabs, setTabs] = React.useState<ITab[]>([]);
  const [activeTabId, setActiveTabId] = React.useState<number>();
  const [isReviewTabVisibleOnMobile, toggleIsReviewTabVisibleOnMobile] =
    React.useReducer((prev) => !prev, false);

  const selectNext = React.useCallback(() => {
    const currTabIndex = tabs.findIndex((tab) => tab.id === activeTabId);

    let newActiveTab;
    if (currTabIndex + 1 < tabs.length) {
      newActiveTab = tabs[currTabIndex + 1];
    } else {
      newActiveTab = tabs[0];
    }
    newActiveTab.ref.current.focus();
    setActiveTabId(newActiveTab.id);
  }, [activeTabId, tabs]);

  const selectPrevious = React.useCallback(() => {
    const currTabIndex = tabs.findIndex((tab) => tab.id === activeTabId);

    let newActiveTab;
    if (currTabIndex > 0) {
      newActiveTab = tabs[currTabIndex - 1];
    } else {
      newActiveTab = tabs[tabs.length - 1];
    }
    newActiveTab.ref.current.focus();
    setActiveTabId(newActiveTab.id);
  }, [activeTabId, tabs]);

  const register = React.useCallback((tabId: ITab['id'], ref: ITab['ref']) => {
    setTabs((prev) => {
      const newTabs = [...prev, { id: tabId, ref }];
      // sometimes tab is registered later than other tabs (api fetch, etc.)
      // to be able to keep right order for keyboard actions, sort array by ids
      newTabs.sort((a, b) => a.id - b.id);
      return newTabs;
    });
  }, []);

  const value: ITabSwitcherProviderState = React.useMemo(
    () => ({
      tabs,
      activeTabId,
      isReviewTabVisibleOnMobile,
      toggleIsReviewTabVisibleOnMobile,
      selectNext,
      selectPrevious,
      setActiveTab: setActiveTabId,
      register,
    }),
    [
      tabs,
      activeTabId,
      isReviewTabVisibleOnMobile,
      toggleIsReviewTabVisibleOnMobile,
      selectNext,
      selectPrevious,
      setActiveTabId,
      register,
    ]
  );

  return (
    <TabSwitcherContext.Provider value={value}>
      {children}
    </TabSwitcherContext.Provider>
  );
};

export const useTabSwitcher = () => {
  const value = React.useContext(TabSwitcherContext);
  if (!value || Object.keys(value).length === 0) {
    throw Error('useTabSwitcher must be used inside TabSwitcherProvider');
  }

  return value;
};
